import mermaid from 'mermaid';

function resolveMermaidApi() {
  const candidates = [
    mermaid,
    mermaid?.default,
    mermaid?.mermaid,
    globalThis?.mermaid,
    globalThis?.mermaid?.default,
    globalThis?.mermaid?.mermaid
  ];

  return candidates.find((candidate) =>
    candidate &&
    typeof candidate.initialize === 'function' &&
    typeof candidate.render === 'function'
  ) || null;
}

let mermaidApi = resolveMermaidApi();
let initialized = false;
let initializedTheme = null;
const registeredContainers = new Set();
let themeObserver = null;
let themeUpdateQueued = false;

function isDarkMode() {
  return document.body.classList.contains('dark-portal');
}

function getThemeName() {
  return isDarkMode() ? 'dark' : 'default';
}

function findMermaidDirectiveEnd(source, startIdx) {
  let depth = 1;
  let inString = null;
  let escaped = false;
  let i = startIdx;

  while (i < source.length) {
    const char = source[i];

    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (char === "\\") {
        escaped = true;
      } else if (char === inString) {
        inString = null;
      }
      i += 1;
      continue;
    }

    if (char === '"' || char === "'") {
      inString = char;
      i += 1;
      continue;
    }

    if (char === "{") {
      depth += 1;
      i += 1;
      continue;
    }

    if (char === "}") {
      depth -= 1;
      if (depth === 0 && source.slice(i + 1, i + 3) === "%%") {
        return i;
      }
      i += 1;
      continue;
    }

    i += 1;
  }

  return null;
}

function stripMermaidDirectives(source) {
  let output = "";
  let i = 0;

  while (i < source.length) {
    const startIdx = source.indexOf("%%{", i);

    if (startIdx === -1) {
      output += source.slice(i);
      break;
    }

    output += source.slice(i, startIdx);
    const closingIdx = findMermaidDirectiveEnd(source, startIdx + 3);

    if (closingIdx === null) {
      output += source.slice(startIdx);
      break;
    }

    i = closingIdx + 3;
  }

  return output;
}

export function sanitizeSource(raw) {
  return stripMermaidDirectives(raw)
    .replace(/^\s*<pre[^>]*>/i, '')
    .replace(/<\/pre>\s*$/i, '')
    .trim();
}

export function initMermaid() {
  const theme = getThemeName();
  if (initialized && initializedTheme === theme) return;

  mermaidApi = mermaidApi || resolveMermaidApi();
  if (!mermaidApi) {
    throw new Error('Mermaid API initialize() is unavailable');
  }

  mermaidApi.initialize({
    startOnLoad: false,
    securityLevel: 'strict',
    maxTextSize: 50000,
    suppressErrorRendering: false,
    htmlLabels: false,
    theme: theme,
    fontFamily: 'inherit',
    state: { useMaxWidth: false },
  });

  initialized = true;
  initializedTheme = theme;
}

export function getMermaidApi() {
  mermaidApi = mermaidApi || resolveMermaidApi();
  return mermaidApi;
}

export async function renderMermaidBlocks(container) {
  const preBlocks = Array.from(container.querySelectorAll('pre.language-mermaid, pre.mermaid'));
  const codeBlocks = Array.from(container.querySelectorAll('pre > code.language-mermaid, pre > code.mermaid'));

  codeBlocks.forEach((codeEl) => {
    const preEl = codeEl.parentElement;
    if (preEl && !preBlocks.includes(preEl)) preBlocks.push(preEl);
  });

  if (preBlocks.length === 0) return;

  initMermaid();

  for (const preEl of preBlocks) {
    if (!preEl) continue;

      const codeEl = preEl.querySelector('code.language-mermaid, code.mermaid');
      const source = sanitizeSource((codeEl || preEl).textContent || '');
      if (!source) continue;

      const wrapper = document.createElement('div');
      wrapper.className = 'mermaid-diagram'; 
      wrapper.setAttribute('contenteditable', 'false');
      wrapper.dataset.mermaidSource = source;

      const id = 'mermaid-' + Math.random().toString(36).slice(2, 10);

    try {
      const { svg } = await mermaidApi.render(id, source);
      wrapper.innerHTML = svg;
    } catch (err) {
      wrapper.classList.add('mermaid-diagram--error');
      wrapper.textContent = 'Diagram error: ' + (err.message || 'Invalid syntax');

      const errorEl = document.getElementById('d' + id);
      if (errorEl) errorEl.remove();
    }

    preEl.replaceWith(wrapper);
  }
}

async function rerenderExistingDiagrams(container) {
  const wrappers = Array.from(container.querySelectorAll('.mermaid-diagram[data-mermaid-source]'));
  if (wrappers.length === 0) return;

  initMermaid();

  for (const wrapper of wrappers) {
    const source = sanitizeSource(wrapper.dataset.mermaidSource || '');
    if (!source) continue;

    const id = 'mermaid-' + Math.random().toString(36).slice(2, 10);

    try {
      const { svg } = await mermaidApi.render(id, source);
      wrapper.classList.remove('mermaid-diagram--error');
      wrapper.innerHTML = svg;
    } catch (err) {
      wrapper.classList.add('mermaid-diagram--error');
      wrapper.textContent = 'Diagram error: ' + (err.message || 'Invalid syntax');

      const errorEl = document.getElementById('d' + id);
      if (errorEl) errorEl.remove();
    }
  }
}

async function rerenderRegisteredContainers() {
  for (const container of Array.from(registeredContainers)) {
    if (!container || !container.isConnected) {
      registeredContainers.delete(container);
      continue;
    }

    await rerenderExistingDiagrams(container);
  }
}

function queueThemeUpdate() {
  if (themeUpdateQueued) return;
  themeUpdateQueued = true;

  requestAnimationFrame(async () => {
    themeUpdateQueued = false;

    const theme = getThemeName();
    if (initializedTheme === theme) return;

    initialized = false;
    initMermaid();
    await rerenderRegisteredContainers();
  });
}

function ensureThemeObserver() {
  if (themeObserver) return;

  themeObserver = new MutationObserver(() => {
    queueThemeUpdate();
  });

  themeObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
  });
}

export function registerMermaidContainer(container) {
  if (!container) return;
  registeredContainers.add(container);
  ensureThemeObserver();
}

export function unregisterMermaidContainer(container) {
  if (!container) return;
  registeredContainers.delete(container);
}
